import type posthogModule from "posthog-js";

type AnalyticsProperties = Record<
  string,
  string | number | boolean | null | undefined
>;

type AnalyticsUser = {
  id: string;
  role?: string | null;
  subscriptionPlan?: string | null;
  subscriptionStatus?: string | null;
  hasActiveAccess?: boolean;
};

type ClarityFunction = ((...args: unknown[]) => void) & {
  q?: unknown[][];
};

declare global {
  interface Window {
    clarity?: ClarityFunction;
  }
}

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ||
  "https://us.i.posthog.com";
const CLARITY_PROJECT_ID = import.meta.env.VITE_CLARITY_PROJECT_ID as
  | string
  | undefined;
const IS_TEST = import.meta.env.MODE === "test";
const ATTRIBUTION_FIRST_KEY = "twap:analytics:first-touch";
const ATTRIBUTION_LAST_KEY = "twap:analytics:last-touch";
const ANALYTICS_BOOT_DELAY_MS = 6000;

let analyticsStarted = false;
let posthogStarted = false;
let analyticsLoadScheduled = false;
const seenSections = new Set<string>();
type PostHogClient = typeof posthogModule;
type PostHogAction = (client: PostHogClient) => void;
let posthogClient: PostHogClient | null = null;
const pendingPostHogActions: PostHogAction[] = [];

function canUseBrowser() {
  return (
    !IS_TEST &&
    typeof window !== "undefined" &&
    typeof document !== "undefined"
  );
}

function clean(value: string | null | undefined) {
  const next = value?.trim();
  return next ? next.slice(0, 120) : undefined;
}

function readAttributionFromUrl() {
  if (!canUseBrowser()) return {};
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: clean(params.get("utm_source")),
    utm_medium: clean(params.get("utm_medium")),
    utm_campaign: clean(params.get("utm_campaign")),
    utm_content: clean(params.get("utm_content")),
    utm_term: clean(params.get("utm_term")),
    referrer: clean(document.referrer),
  };
}

function compactProperties(properties: AnalyticsProperties) {
  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== undefined),
  );
}

function storeAttribution() {
  if (!canUseBrowser()) return;
  const attribution = compactProperties(readAttributionFromUrl());
  if (Object.keys(attribution).length === 0) return;

  const payload = JSON.stringify({
    ...attribution,
    landing_path: window.location.pathname,
    captured_at: new Date().toISOString(),
  });

  try {
    if (!window.localStorage.getItem(ATTRIBUTION_FIRST_KEY)) {
      window.localStorage.setItem(ATTRIBUTION_FIRST_KEY, payload);
    }
    window.localStorage.setItem(ATTRIBUTION_LAST_KEY, payload);
  } catch {
    /* noop */
  }
}

function readStoredAttribution(key: string) {
  if (!canUseBrowser()) return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || "{}");
    return parsed && typeof parsed === "object"
      ? (parsed as AnalyticsProperties)
      : {};
  } catch {
    return {};
  }
}

function withAttribution(properties: AnalyticsProperties = {}) {
  return compactProperties({
    ...properties,
    first_utm_source: readStoredAttribution(ATTRIBUTION_FIRST_KEY).utm_source,
    first_utm_medium: readStoredAttribution(ATTRIBUTION_FIRST_KEY).utm_medium,
    first_utm_campaign: readStoredAttribution(ATTRIBUTION_FIRST_KEY).utm_campaign,
    first_utm_content: readStoredAttribution(ATTRIBUTION_FIRST_KEY).utm_content,
    last_utm_source: readStoredAttribution(ATTRIBUTION_LAST_KEY).utm_source,
    last_utm_medium: readStoredAttribution(ATTRIBUTION_LAST_KEY).utm_medium,
    last_utm_campaign: readStoredAttribution(ATTRIBUTION_LAST_KEY).utm_campaign,
    last_utm_content: readStoredAttribution(ATTRIBUTION_LAST_KEY).utm_content,
  });
}

function loadClarity(projectId: string) {
  if (!canUseBrowser() || window.clarity) return;

  window.clarity = (...args: unknown[]) => {
    (window.clarity!.q = window.clarity!.q || []).push(args);
  };

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${projectId}`;
  document.head.appendChild(script);
}

function flushPostHogActions() {
  if (!posthogClient) return;
  while (pendingPostHogActions.length > 0) {
    const action = pendingPostHogActions.shift();
    if (action) action(posthogClient);
  }
}

function withPostHog(action: PostHogAction) {
  if (posthogClient) {
    action(posthogClient);
    return;
  }
  if (POSTHOG_KEY) pendingPostHogActions.push(action);
}

function loadPostHog() {
  if (!POSTHOG_KEY) return;
  void import("posthog-js").then(({ default: posthog }) => {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      autocapture: false,
      capture_dead_clicks: false,
      capture_pageview: false,
      disable_session_recording: true,
      disable_surveys: true,
      disable_surveys_automatic_display: true,
      person_profiles: "identified_only",
      request_batching: false,
    });
    posthogClient = posthog;
    posthogStarted = true;
    flushPostHogActions();
  });
}

function scheduleThirdPartyAnalytics() {
  if (analyticsLoadScheduled) return;
  analyticsLoadScheduled = true;

  window.setTimeout(() => {
    const load = () => {
      loadPostHog();
      if (CLARITY_PROJECT_ID) loadClarity(CLARITY_PROJECT_ID);
    };

    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(load, { timeout: 4000 });
      return;
    }

    load();
  }, ANALYTICS_BOOT_DELAY_MS);
}

export function initAnalytics() {
  if (!canUseBrowser() || analyticsStarted) return;
  analyticsStarted = true;
  storeAttribution();
  scheduleThirdPartyAnalytics();
}

export function trackPageView(path: string) {
  storeAttribution();
  const properties = withAttribution({
    path,
    url: canUseBrowser() ? window.location.href : path,
  });
  withPostHog((posthog) => posthog.capture("$pageview", properties));
  window.clarity?.("set", "page_path", path);
}

export function trackAnalyticsEvent(
  name: string,
  properties: AnalyticsProperties = {},
) {
  const nextProperties = withAttribution(properties);
  withPostHog((posthog) => posthog.capture(name, nextProperties));
  window.clarity?.("event", name);
}

export function trackLandingSectionSeen(section: string) {
  const key = `landing:${section}`;
  if (seenSections.has(key)) return;
  seenSections.add(key);
  trackAnalyticsEvent("landing_section_seen", { section });
  window.clarity?.("set", "last_landing_section", section);
}

export function identifyAnalyticsUser(user: AnalyticsUser) {
  if (!user.id) return;
  withPostHog((posthog) => {
    posthog.identify(user.id, {
      role: user.role ?? "user",
      subscription_plan: user.subscriptionPlan ?? "none",
      subscription_status: user.subscriptionStatus ?? "free",
      has_active_access: Boolean(user.hasActiveAccess),
    });
  });
}

export function resetAnalyticsUser() {
  withPostHog((posthog) => posthog.reset());
}
